/* eslint-disable no-multi-assign */ // ok in reducers
import * as pages from 'constants/pages'
import * as types from 'constants/action_types'
import * as draftActionTypes from 'ducks/drafts/types'
import storage from 'util/storage'

import {
  constructSearchQueryObject,
  constructSearchQueryId,
  constructSearchQueryTitle,
} from 'util/search'
import amendSearchQueryString from 'util/search/amendSearchQueryString'
import positionAfterAmendSearchQueryString from 'util/search/positionAfterAmendSearchQueryString'
import { any, emptyArr, getLength, without, diff, uniq } from 'util/arrays'
import { mapAggregationsToSearches } from 'util/search/counts'
import metrics from 'util/metrics'
import debug from 'util/debug'

const reducers = {}
const initialLastSearchQueryId = storage.get('lastSearchQueryId')
// remove the lastSearchQueryId
storage.remove('lastSearchQueryId')

const defaultState = {
  byQueryId: {},
  initialLastSearchQueryId,
  mergeQueryString: null,
  uncommittedSearchQueryString: null,
  committedSearchQueryString: null,
}

reducers[types.MARK_FETCHING_STATUS] = (state, action) => {
  const data = action.data
  if (data.action === 'fetchTicketsByKeyword') {
    let isFetching = state.isFetching || false
    isFetching = data.status
    return Object.assign({}, state, {
      isFetching,
    })
  }
  return state
}

reducers[types.SEARCH_SUCCESS] = reducers[types.MERGEABLE_SEARCH_SUCCESS] = (
  state,
  action
) => {
  const {
    tickets,
    queryId,
    queryObject,
    currentPage,
    totalCount,
    totalPages,
    sortOrder,
  } = action.data
  const search = Object.assign(
    {
      pages: {},
      highlights: {},
    },
    state.byQueryId[queryId],
    {
      queryId,
      queryObject,
      totalCount,
      currentPage,
      totalPages,
      sortOrder,
      loaded: true,
      errored: false,
      resultsStale: false,
      resultsIncomplete: false,
      removedTicketIds: {},
      addedTicketIds: {},
      isNamed: !!constructSearchQueryTitle(queryObject) !== null,
    }
  )
  const ticketIds = tickets.map(t => t.id)
  search.pages = Object.assign({}, search.pages, {
    [currentPage]: ticketIds,
  })

  const areResultsStale =
    state.byQueryId[queryId] && state.byQueryId[queryId].resultsStale

  // (pnagy) If we already have the ticket list loaded bust it's flagged as stale
  // we need to simply exchange it to the newly received list so we have a new list.
  // We can't empty the list in the reducer when we do the stale flagging because that would
  // lead to the rendering of an empty list and that messes up the scroll position and ugly as
  // it's visible by the user
  if (search.sortedIds && areResultsStale) {
    search.sortedIds = ticketIds
  } else {
    // if we're on the current search, it will not re-sort
    // so we consider the sort order to be correct and add to the list
    search.sortedIds = uniq([].concat(search.sortedIds || [], ticketIds))
  }
  const highlights = tickets.reduce((result, t) => {
    if (t.search_summary) result[t.id] = t.search_summary // eslint-disable-line no-param-reassign
    return result
  }, {})
  search.highlights = Object.assign({}, search.highlights, highlights)
  search.resultsIncomplete = false

  const newByQuery = Object.assign({}, state.byQueryId, {
    [queryId]: search,
  })

  return Object.assign({}, state, {
    byQueryId: newByQuery,
  })
}

reducers[types.MARK_SEARCH_AS_COMPLETE] = (state, action) => {
  const { queryId } = action.data
  const search = {
    ...state.byQueryId[queryId],
    resultsIncomplete: false,
  }
  const newByQuery = { ...state.byQueryId, [queryId]: search }
  return { ...state, byQueryId: newByQuery }
}

reducers[types.SEARCH_BY_TICKET_NUMBER_SUCCESS] = (state, action) => {
  const { ticketId } = action.data
  const search = {
    ...state.byQueryId[ticketId],
  }

  if (search.pages) {
    search.pages = {
      ...search.pages,
      1: [ticketId].concat(search.pages[1]),
    }
  } else {
    search.pages = { 1: [ticketId] }
  }

  if (search.sortedIds) {
    search.sortedIds = uniq([ticketId].concat(search.sortedIds))
  } else {
    search.sortedIds = [ticketId]
  }

  const newByQuery = {
    ...state.byQueryId,
    [ticketId]: search,
  }

  return {
    ...state,
    byQueryId: newByQuery,
  }
}

reducers[types.SEARCH_ERROR] = (state, action) => {
  const { queryId } = action.data
  const search = Object.assign({}, state.byQueryId[queryId], {
    errored: true,
  })

  const newByQuery = Object.assign({}, state.byQueryId, {
    [queryId]: search,
  })
  return Object.assign({}, state, {
    byQueryId: newByQuery,
  })
}

reducers[types.SEARCH_CLEAR_TICKET_RESULTS] = (state, action) => {
  const {
    valueIdMap,
    omitNamedSearches,
    queryIdToKeep,
    clearCounts,
    refetchListOnNextUpdate,
  } = action.data
  // clear the results cache
  const { byQueryId } = state
  return {
    ...state,
    byQueryId: Object.keys(byQueryId).reduce((newByQueryId, queryId) => {
      const queryTitle = constructSearchQueryTitle(queryId, valueIdMap)
      const shouldKeep = queryId === queryIdToKeep
      const isNamedSoWeLeaveIt = queryTitle && omitNamedSearches
      const clearQueryResult = !shouldKeep && !isNamedSoWeLeaveIt
      const leaveForNowButFlagAsStale =
        shouldKeep && !isNamedSoWeLeaveIt && refetchListOnNextUpdate
      return {
        ...newByQueryId,
        [queryId]: {
          ...byQueryId[queryId],
          ...(clearQueryResult && {
            pages: null,
            resultsStale: true,
            sortedIds: undefined,
          }),
          ...(leaveForNowButFlagAsStale && { resultsStale: true }),
          ...(clearCounts && { totalCount: null }),
        },
      }
    }, {}),
    openMergableByCustomerId: {},
    closedMergableByCustomerId: {},
    mergeQueryString: null,
  }
}

reducers[types.SEARCH_CLEAR_ALL] = state => {
  const { byQueryId } = state
  return {
    ...state,
    byQueryId: Object.keys(byQueryId).reduce((newByQueryId, queryId) => {
      const search = byQueryId[queryId]
      // no need to do anything when search is not loaded
      if (search.loaded) return newByQueryId
      const clearedSearch = {
        ...byQueryId[queryId],
        pages: null,
        sortedIds: undefined,
        loaded: false,
      }
      // this flag removal is important to show the spinner
      delete search.errored
      return {
        ...newByQueryId,
        [queryId]: clearedSearch,
      }
    }, {}),
  }
}

// A simplified version of SEARCH_CLEAR_TICKET_RESULTS,
// happening only on realtime updates
const clearNonNamedTicketSearches = (state, action) => {
  const { options } = action.data
  const { currentQueryId } = options

  const { byQueryId } = state
  let hasChanges

  Object.keys(byQueryId).forEach(queryId => {
    const search = byQueryId[queryId]
    // no need to do anything to unloaded searches
    if (!search.loaded) return
    // skip named searches
    if (search.isNamed) return
    // also don't touch current search
    if (queryId === currentQueryId) return

    hasChanges = true
    byQueryId[queryId] = {
      ...byQueryId[queryId],
      pages: null,
      loaded: false,
      resultsStale: true,
      sortedIds: undefined,
    }
  })

  if (!hasChanges) return state

  return { ...state, byQueryId }
}
reducers[types.UPDATE_TICKETS] = clearNonNamedTicketSearches
reducers[types.CREATE_CHANGESET_SUCCESS] = clearNonNamedTicketSearches

function updateSearches(
  byQueryId,
  searchIds = [],
  ticketId,
  body,
  currentDraftBody
) {
  return searchIds.reduce((acc, search) => {
    /* eslint-disable no-param-reassign */
    const searchObject = { ...byQueryId[search] }
    let page = null
    let sortedIds
    if (searchObject.sortedIds) {
      sortedIds = [].concat(searchObject.sortedIds)
    }
    const removedTicketIds = { ...(searchObject.removedTicketIds || {}) }
    const addedTicketIds = { ...(searchObject.addedTicketIds || {}) }
    const { sortOrder } = searchObject

    // the page is not necessarily present, but count is
    // if page is present, get a new copy and store it for usage
    // later in the function
    if (searchObject && searchObject.pages && searchObject.pages[1]) {
      searchObject.pages = { ...searchObject.pages }
      searchObject.pages[1] = [].concat(searchObject.pages[1]) // copy the array
      page = searchObject.pages[1]
    }

    if (body && body.length > 0) {
      // add (if not already present)
      if (!currentDraftBody) {
        if (page && page.indexOf(ticketId) < 0) page.push(ticketId)

        // as in the root reducer, we track added(/removed) ticket ids so
        // that we dont apply diffs (e.g. from realtime updates) twice
        if (!addedTicketIds[ticketId]) {
          searchObject.totalCount += 1
          addedTicketIds[ticketId] = true
          delete removedTicketIds[ticketId]
        }

        if (sortedIds && sortedIds.indexOf(ticketId) < 0) {
          if (sortOrder === 'oldest') {
            sortedIds.push(ticketId)
          } else {
            sortedIds = [ticketId].concat(sortedIds)
          }
        }
      }
    } else {
      // remove
      if (page && page.indexOf(ticketId) > -1) without(page, ticketId)

      if (!removedTicketIds[ticketId]) {
        searchObject.totalCount -= 1
        removedTicketIds[ticketId] = true
        delete addedTicketIds[ticketId]
      }

      if (sortedIds && sortedIds.indexOf(ticketId) > -1) {
        without(sortedIds, ticketId)
      }
    }

    // negative counts make us look like asses
    if (searchObject.totalCount < 0) searchObject.totalCount = 0
    if (sortedIds) searchObject.sortedIds = sortedIds
    if (addedTicketIds) searchObject.addedTicketIds = addedTicketIds
    if (removedTicketIds) searchObject.removedTicketIds = removedTicketIds

    acc[search] = searchObject

    return acc
    /* eslint-enable no-param-reassign */
  }, {})
}

// if a draft changes, add or remove the ticket from appropriate search
reducers[draftActionTypes.UPDATE_LOCAL_REPLY_DRAFT] = (state, action) => {
  const {
    ticketId,
    currentUserId,
    diff, // eslint-disable-line no-shadow
    currentDraftBody,
    draftFolderIds = emptyArr,
  } = action.data
  const body = diff ? diff.body : null
  const byQueryId = state.byQueryId

  const changedSearchIds = Object.keys(byQueryId).reduce((acc, search) => {
    // we remove from current user's draft search
    const matchesDraftSearch =
      currentUserId && search.match(`draft:${currentUserId}`)
    const matchesDraftFolder = any(
      id => search.match(`folder:${id}`),
      draftFolderIds
    )
    if (matchesDraftSearch || matchesDraftFolder) acc.push(search)
    return acc
  }, [])

  if (getLength(changedSearchIds) === 0) return state

  const updatedSearches = updateSearches(
    state.byQueryId,
    changedSearchIds,
    ticketId,
    body,
    currentDraftBody
  )

  return {
    ...state,
    byQueryId: {
      ...byQueryId,
      ...updatedSearches,
    },
  }
}

reducers[types.SEARCHES_SUCCESS] = (state, action) => {
  const { trackMetrics, metricType, isAutomatic, isManual } = action.data
  const newState = Object.assign({}, state)
  const searches = action.data && action.data.searches
  newState.byQueryId = Object.assign({}, newState.byQueryId)

  let metricOutput
  if (trackMetrics) {
    metricOutput = {
      correct: 0,
      incorrect: 0,
      correct_folder: 0,
      incorrect_folder: 0,
      correct_assignee: 0,
      incorrect_assignee: 0,
      correct_tag: 0,
      incorrect_tag: 0,
    }
  }

  Object.keys(searches).forEach(queryString => {
    const queryId = constructSearchQueryId(queryString)
    const query = newState.byQueryId[queryId]
    const newSearchData = searches[queryString]
    const newCount = newSearchData.count
    const currentCount = query && query.totalCount
    if (trackMetrics) {
      if (currentCount !== undefined && currentCount !== null) {
        const correct = currentCount === newCount
        if (correct) {
          metricOutput.correct += 1
          try {
            if (queryId.match('folder:')) metricOutput.correct_folder += 1
            if (queryId.match('assignee:')) metricOutput.correct_assignee += 1
            if (queryId.match('tag:')) metricOutput.correct_tag += 1
          } catch (e) {
            // pass
          }
        }
        if (!correct) {
          metricOutput.incorrect += 1
          try {
            if (queryId.match('folder:')) metricOutput.incorrect_folder += 1
            if (queryId.match('assignee:')) metricOutput.incorrect_assignee += 1
            if (queryId.match('tag:')) metricOutput.incorrect_tag += 1
          } catch (e) {
            // pass
          }
        }
      }
    }

    const newQuery = {
      ...query,
      timestamp: newSearchData.timestamp,
      queryId,
      queryObject: constructSearchQueryObject(queryString),
      totalCount: newCount,
    }
    // count has changed, which means that something has changed in this search,
    // we need to update it on next load, so we panic and clear the search
    if (query && query.sortedIds && currentCount !== newCount) {
      if (debug.enabled) {
        // eslint-disable-next-line no-console
        console.debug({
          queryId,
          currentCount,
          newCount,
          sortedIdsCount: query.sortedIds.length,
        })
      }
      newQuery.pages = null
      newQuery.resultsIncomplete = true
    }
    newState.byQueryId[queryId] = newQuery
  })

  if (trackMetrics) {
    const payload = {
      logname: 'counts',
      metric: metricType || 'searchCounts',
      ...metricOutput,
      isAutomatic,
      isManual,
    }
    metrics.log(payload)
  }

  return newState
}

reducers[types.FETCH_TICKET_AGGREGATION_COUNTS_SUCCESS] = (
  state,
  {
    data: {
      filteredSearch,
      ticketAggregations,
      trackMetrics,
      isManual,
      isAutomatic,
    },
  }
) => {
  return reducers[types.SEARCHES_SUCCESS](state, {
    data: {
      searches: mapAggregationsToSearches(filteredSearch, ticketAggregations),
      trackMetrics,
      metricType: 'aggregationCounts',
      isManual,
      isAutomatic,
    },
  })
}

reducers[pages.SEARCH_PAGE] = (state, action) => {
  const lastSearchQueryId = action.payload.term
  storage.set('lastSearchQueryId', lastSearchQueryId)
  return {
    ...state,
    lastSearchQueryId,
    isTypedSearch: action.payload.isTypedSearch,
  }
}

reducers[types.STORE_LATEST_SEARCH] = (state, action) => {
  return Object.assign({}, state, {
    lastSearchQueryId: action.data.queryId,
  })
}

reducers[types.UPDATE_APP_DATA] = (state, action) => {
  return Object.assign({}, state, {
    lastSearchQueryId: action.data.latestQueryId,
  })
}

reducers[types.DELETE_TICKETS_SUCCESS] = reducers[
  types.DELETE_TICKETS_STARTED
] = (state, action) => {
  const data = action.data
  const tickets = data.tickets
  const ticketIds = tickets.map(t => t.id)

  return removeTickets(state, ticketIds)
}

const withFolder = id => str => str.match(`folder:${id}`)

reducers[draftActionTypes.INVALIDATE_REPLY_DRAFT] = (state, action) => {
  const { ticketId, draftFolderIds = [] } = action.data

  return draftFolderIds.reduce((acc, id) => {
    return removeTickets(acc, ticketId, withFolder(id))
  }, state)
}

function removeTickets(state, ticketIds, predicateFn = f => f) {
  const byQueryId = state.byQueryId || {}
  const newByQueryId = { ...byQueryId }
  const changedIds = Object.keys(newByQueryId).filter(predicateFn)

  if (getLength(changedIds) === 0) return state

  changedIds.forEach(id => {
    const search = (newByQueryId[id] = Object.assign({}, newByQueryId[id]))
    if (search.pages) {
      const searchPages = (search.pages = Object.assign({}, search.pages))
      Object.keys(searchPages).forEach(pageNumber => {
        const page = searchPages[pageNumber]
        searchPages[pageNumber] = diff(page, ticketIds)
      })
    }
    if (search.sortedIds) {
      search.sortedIds = diff(search.sortedIds || [], ticketIds)
    }
  })

  return {
    ...state,
    byQueryId: newByQueryId,
  }
}

reducers[types.MERGE_TICKETS_REQUEST] = reducers[
  types.MERGE_TICKETS_SUCCESS
] = (state, action) => {
  if (!action.data) return state
  const {
    data: { mergedTickets },
  } = action
  return reducers[types.DELETE_TICKETS_STARTED](state, {
    data: mergedTickets,
  })
}

reducers[pages.TICKET_MERGE_TERM_PAGE] = (state, { payload: { term } }) => {
  return {
    ...state,
    mergeQueryString: decodeURIComponent(term),
  }
}

reducers[pages.TICKET_MERGE_PAGE] = state => {
  return {
    ...state,
    mergeQueryString: null,
  }
}

reducers[pages.TICKET_PAGE] = (state, { payload: { id }, meta }) => {
  const newState = {
    ...state,
    mergeQueryString: null,
  }

  let prevId

  if (
    meta &&
    meta.location &&
    meta.location.prev &&
    meta.location.prev.payload
  ) {
    prevId = meta.location.prev.payload.id
  }

  const ticketPathChanged = id !== prevId

  if (!ticketPathChanged) return state

  return newState
}

reducers[types.UPDATE_CURRENT_TICKET_SEARCH_QUERY] = (state, action) => {
  const {
    data: {
      amend,
      commit,
      position,
      queryString,
      selectionEnd,
      selectionStart,
      submit,
      reset,
      isTypedSearch,
    },
  } = action
  const { committedSearchQueryString, uncommittedSearchQueryString } = state
  const newQueryString = amend
    ? amendSearchQueryString(
        uncommittedSearchQueryString,
        queryString,
        selectionStart,
        commit
      )
    : queryString
  const selectionAfterAmend =
    amend &&
    positionAfterAmendSearchQueryString(
      uncommittedSearchQueryString,
      queryString,
      selectionStart,
      commit
    )
  const selection = amend
    ? {
        selectionEnd: selectionAfterAmend,
        selectionStart: selectionAfterAmend,
      }
    : {
        selectionEnd: position || selectionEnd,
        selectionStart: position || selectionStart,
      }

  if (!queryString && queryString !== '') {
    return { ...state, ...selection, isTypedSearch }
  }

  if (reset || submit)
    return {
      ...state,
      ...selection,
      committedSearchQueryString: null,
      uncommittedSearchQueryString: null,
      isTypedSearch,
    }

  const newCommittedSearchQueryString = commit
    ? newQueryString
    : committedSearchQueryString

  return {
    ...state,
    ...selection,
    committedSearchQueryString: newCommittedSearchQueryString,
    uncommittedSearchQueryString: newQueryString,
    isTypedSearch,
  }
}

reducers[types.TOGGLE_LIST_SEARCH_BOX_STATUS] = (state, action) => {
  if (!action.data) return state
  const {
    data: { isFocused },
  } = action

  return {
    ...state,
    listSearchBoxFocused: isFocused,
  }
}

export default function reducer(state = defaultState, action) {
  // this is here because a long reducer with many ifs is unreadable
  const handler = reducers[action.type]
  if (handler) return handler(state, action)
  return state
}
